import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import db from '../data/db';
import { getCardCounts } from '../data/fsrs';
import EmptyState from './EmptyState';
import { FiPlay, FiPlus, FiArrowLeft, FiTrash2, FiEdit2, FiLock } from 'react-icons/fi';
import 'katex/dist/katex.min.css';

export default function DeckView() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [cards, setCards] = useState([]);
  const [counts, setCounts] = useState({ newCount: 0, learningCount: 0, dueCount: 0, total: 0 });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    loadDeck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId]);

  async function loadDeck() {
    const d = await db.decks.get(Number(deckId));
    setDeck(d);
    const c = await db.cards.where('deckId').equals(Number(deckId)).toArray();
    setCards(c);
    setCounts(getCardCounts(c));
  }

  async function deleteCard(cardId) {
    await db.cards.delete(cardId);
    setConfirmDeleteId(null);
    loadDeck();
  }

  if (!deck) return <div className="page"><div className="loading-spinner" /></div>;

  const hasDue = counts.newCount + counts.learningCount + counts.dueCount > 0;
  const isSystem = !!deck.isSystem;

  return (
    <div className="page">
      <button className="btn btn-ghost back-btn" onClick={() => navigate('/')}>
        <FiArrowLeft /> Back to Decks
      </button>

      <div className="deck-header">
        <div className="deck-header-info">
          <span className="deck-emoji-lg">{deck.emoji || '📖'}</span>
          <div>
            <h1>
              {deck.name}
              {isSystem && (
                <span
                  className="library-badge"
                  title="Library deck — content is curated and cannot be modified"
                  style={{
                    marginLeft: 'var(--space-sm)',
                    fontSize: '0.7rem',
                    color: 'var(--text-tertiary)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    border: '1px solid var(--border-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    verticalAlign: 'middle',
                  }}
                >
                  <FiLock size={11} /> Library
                </span>
              )}
            </h1>
            {deck.description && <p className="deck-description">{deck.description}</p>}
          </div>
        </div>

        <div className="deck-header-actions">
          {hasDue && (
            <button className="btn btn-primary" onClick={() => navigate(`/study/${deckId}`)}>
              <FiPlay /> Study ({counts.newCount + counts.learningCount + counts.dueCount} cards)
            </button>
          )}
          {!isSystem && (
            <button className="btn btn-secondary" onClick={() => navigate(`/deck/${deckId}/add`)}>
              <FiPlus /> Add Cards
            </button>
          )}
        </div>
      </div>

      <div className="deck-stats-bar">
        <div className="stat-item">
          <span className="stat-value count-new">{counts.newCount}</span>
          <span className="stat-label">New</span>
        </div>
        <div className="stat-item">
          <span className="stat-value count-learning">{counts.learningCount}</span>
          <span className="stat-label">Learning</span>
        </div>
        <div className="stat-item">
          <span className="stat-value count-due">{counts.dueCount}</span>
          <span className="stat-label">Due</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{counts.total}</span>
          <span className="stat-label">Total</span>
        </div>
      </div>

      {cards.length === 0 ? (
        <EmptyState
          icon="🃏"
          title="No cards yet"
          description={isSystem ? "This Library deck is empty." : "Add some flashcards to this deck to start studying."}
          actionLabel={isSystem ? undefined : "Add Your First Card"}
          onAction={isSystem ? undefined : () => navigate(`/deck/${deckId}/add`)}
        />
      ) : (
        <div className="card-list">
          <div className="card-list-header">
            <h3>Cards ({cards.length})</h3>
          </div>
          {cards.map(card => (
            <div key={card.id} className="card-list-item">
              <div className="card-list-content">
                <div className="card-front-preview">
                  <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                    {truncate(card.front, 80)}
                  </ReactMarkdown>
                </div>
                <div className="card-back-preview">
                  <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                    {truncate(card.back, 60)}
                  </ReactMarkdown>
                </div>
              </div>
              {!isSystem && (
                <div className="card-list-actions">
                  <button
                    className="btn btn-icon btn-ghost"
                    onClick={() => navigate(`/deck/${deckId}/edit/${card.id}`)}
                    aria-label="Edit card"
                    title="Edit card"
                  >
                    <FiEdit2 />
                  </button>
                  {confirmDeleteId === card.id ? (
                    <>
                      <button
                        className="btn btn-icon btn-ghost danger"
                        onClick={() => deleteCard(card.id)}
                        aria-label="Confirm delete card"
                        title="Confirm delete"
                        style={{ color: 'var(--color-danger, #ef4444)' }}
                      >
                        <FiTrash2 />
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setConfirmDeleteId(null)}
                        aria-label="Cancel delete"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-icon btn-ghost danger"
                      onClick={() => setConfirmDeleteId(card.id)}
                      aria-label="Delete card"
                      title="Delete card"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function truncate(text, max) {
  if (!text) return '';
  return text.length > max ? `${text.substring(0, max)}…` : text;
}
